import "./CourseOfSalesWidgetComponent.scss";
import template from "./CourseOfSalesWidgetComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {BehaviorSubject, ReplaySubject} from "rxjs";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {CourseOfSalesWidgetConfig} from "./models/CourseOfSalesWidgetConfig";
import {CodeAndExchange} from "../../../../../instruments/types/CodeAndExchange";
import {WidgetDisplayMode} from "../../../models/WidgetDisplayMode";

export const CourseOfSalesWidget = "tfCourseOfSalesWidget";

export function courseOfSalesWidget(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<",
            config: "<",
            displayMode: "<",
            onSecurityChanged: "&",
            onConfigChanged: "&"
        },
        controllerAs: "ctrl",
        controller: CourseOfSalesWidgetComponentController
    };
}

export class CourseOfSalesWidgetComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    /** @input */
    public set config(config: CourseOfSalesWidgetConfig | null) {
        if (config != null) {
            this.config$.next(config);
        }
    }

    private readonly config$ = new BehaviorSubject<CourseOfSalesWidgetConfig | null>(null);

    /** @input */
    public set displayMode(displayMode: WidgetDisplayMode | null) {
        if (displayMode != null) {
            this.displayMode$.next(displayMode);
        }
    }

    private readonly displayMode$ = new ReplaySubject<WidgetDisplayMode>(1);

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    /** @output */
    public readonly onConfigChanged: ComponentCallback;

    public readonly currentSecurity$ = new ReplaySubject<CodeAndExchange>(1);

    private readonly securityChanged$ = new ReplaySubject<CodeAndExchange>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public $onInit(): void {
        this.watchSecurity();
        this.watchConfig();
        this.watchSecurityChanged();
    }

    private watchSecurity() {
        this.security$
            .takeUntil(this.destroyed$)
            .distinctUntilChanged()
            .withLatestFrom(this.config$)
            .map(([security, config]) => this.updateConfig(security, config))
            .subscribe((config) => this.configUpdated(config));
    }

    private watchConfig() {
        this.config$
            .takeUntil(this.destroyed$)
            .filter(isNotNull)
            .map((c) => c.selected)
            .subscribe((security) => this.currentSecurity$.next(security));
    }

    private watchSecurityChanged() {
        this.securityChanged$
            .takeUntil(this.destroyed$)
            .distinctUntilChanged()
            .withLatestFrom(this.config$)
            .map(([security, config]) => this.updateConfig(security, config))
            .subscribe((config) => this.configUpdated(config));
    }

    private updateConfig(security: CodeAndExchange, config: CourseOfSalesWidgetConfig | null): CourseOfSalesWidgetConfig {
        if (config == null) {
            return {selected: security};
        } else {
            return {...config, selected: security};
        }
    }

    private configUpdated(config: CourseOfSalesWidgetConfig) {
        this.onConfigChanged({config});
    }

    public securityChanged(security: CodeAndExchange) {
        this.securityChanged$.next(security);
        this.onSecurityChanged({security});
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
